import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api, { getImageUrl, API_BASE } from '../../utils/api';
import { toast } from 'react-toastify';
import AdminLayout from '../../components/admin/AdminLayout';

interface IconData {
    _id: string;
    name: string;
    icon: string;
}

const AdminIcons: React.FC = () => {
    const { token, user } = useAuth();
    const navigate = useNavigate();
    const [icons, setIcons] = useState<IconData[]>([]);
    const [loading, setLoading] = useState(true);

    // Dialog state
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentId, setCurrentId] = useState('');
    const [name, setName] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [iconPreview, setIconPreview] = useState('');

    useEffect(() => {
        if (!token || user?.role !== 'admin') {
            navigate('/login');
            return;
        }
        fetchIcons();
    }, [token, user, navigate]);

    const fetchIcons = async () => {
        try {
            setLoading(true);
            const data = await api.getIcons();
            setIcons(data);
        } catch (error) {
            console.error('Error fetching icons:', error);
            toast.error('Failed to load icons');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (iconData?: IconData) => {
        if (iconData) {
            setEditMode(true);
            setCurrentId(iconData._id);
            setName(iconData.name);
            setIconPreview(iconData.icon);
            setFile(null);
        } else {
            setEditMode(false);
            setCurrentId('');
            setName('');
            setIconPreview('');
            setFile(null);
        }
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setName('');
        setIconPreview('');
        setFile(null);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            setIconPreview(URL.createObjectURL(selectedFile));
        }
    };

    const handleUpload = async (files: File[]) => {
        if (!token || files.length === 0) return [];
        try {
            const formData = new FormData();
            files.forEach(file => formData.append('images', file));
            
            const res = await fetch(`${API_BASE}/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            
            const data = await res.json();
            if (!res.ok) {
                toast.error(`Upload failed: ${data.message || 'Server error'}`);
                return [];
            }
            return data.urls || [];
        } catch (err) {
            console.error('Upload failed', err);
            toast.error('Connection error during upload');
            return [];
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!name) {
            toast.error('Please provide a name for the icon');
            return;
        }
        if (!editMode && !file) {
            toast.error('Please upload an icon image');
            return;
        }

        try {
            let finalIconUrl = iconPreview;
            if (file) {
                const urls = await handleUpload([file]);
                if (urls && urls.length > 0) {
                    finalIconUrl = urls[0];
                }
            }

            if (editMode) {
                await api.adminUpdateIcon(token as string, currentId, { name, icon: finalIconUrl });
                toast.success('Icon updated successfully');
            } else {
                await api.adminCreateIcon(token as string, { name, icon: finalIconUrl });
                toast.success('Icon created successfully');
            }
            
            handleCloseDialog();
            fetchIcons();
        } catch (error) {
            console.error('Error saving icon:', error);
            toast.error('Failed to save icon');
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this icon?')) return;
        try {
            await api.adminDeleteIcon(token as string, id);
            toast.success('Icon deleted');
            fetchIcons();
        } catch (error) {
            console.error('Error deleting icon:', error);
            toast.error('Failed to delete icon');
        }
    };

    return (
        <AdminLayout>
            <div className="bg-slate-50 min-h-screen p-4 md:p-8">
                <div className="max-w-7xl mx-auto">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                        <div>
                            <h1 className="text-2xl font-black text-[#05144b] mb-1">Icon Library</h1>
                            <p className="text-sm text-slate-500 font-medium">Manage custom image icons for amenities, highlights, and specifications.</p>
                        </div>
                        <button 
                            onClick={() => handleOpenDialog()}
                            className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2.5 rounded-xl font-bold shadow-lg shadow-blue-600/20 transition-all flex items-center justify-center gap-2"
                        >
                            <i className="fa-solid fa-plus"></i> Add New Icon
                        </button>
                    </div>

                    {/* Icon Grid */}
                    {loading ? (
                        <div className="flex justify-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                        </div>
                    ) : icons.length === 0 ? (
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 text-center">
                            <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-slate-100">
                                <i className="fa-regular fa-image text-2xl text-slate-400"></i>
                            </div>
                            <h3 className="text-lg font-bold text-[#05144b] mb-1">No Icons Found</h3>
                            <p className="text-slate-500 text-sm">Upload your first custom icon to start building your library.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                            {icons.map(icon => (
                                <div key={icon._id} className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-col items-center relative overflow-hidden">
                                    <div className="absolute top-2 right-2 flex gap-1 z-10">
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleOpenDialog(icon);
                                            }}
                                            className="w-7 h-7 bg-blue-100 text-blue-700 rounded-md flex items-center justify-center hover:bg-blue-600 hover:text-white transition-colors shadow-sm"
                                            title="Edit"
                                        >
                                            <i className="fa-solid fa-pen text-[10px]"></i>
                                        </button>
                                        <button 
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDelete(icon._id);
                                            }}
                                            className="w-7 h-7 bg-rose-100 text-rose-700 rounded-md flex items-center justify-center hover:bg-rose-600 hover:text-white transition-colors shadow-sm"
                                            title="Delete"
                                        >
                                            <i className="fa-solid fa-trash text-[10px]"></i>
                                        </button>
                                    </div>
                                    <div className="w-16 h-16 mb-3 flex items-center justify-center p-2 bg-slate-50 rounded-lg border border-slate-100">
                                        <img 
                                            src={getImageUrl(icon.icon)} 
                                            alt={icon.name} 
                                            className="max-w-full max-h-full object-contain"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                target.parentElement?.insertAdjacentHTML('beforeend', '<i class="fa-solid fa-image text-slate-300 text-xl"></i>');
                                            }}
                                        />
                                    </div>
                                    <span className="text-[11px] font-bold text-slate-700 text-center w-full truncate" title={icon.name}>{icon.name}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Dialog Box Modal */}
            {isDialogOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden animate-fade-in-up">
                        <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50">
                            <h3 className="text-lg font-bold text-[#05144b]">{editMode ? 'Edit Icon' : 'Add New Icon'}</h3>
                            <button onClick={handleCloseDialog} className="text-slate-400 hover:text-slate-600 transition-colors">
                                <i className="fa-solid fa-xmark text-lg"></i>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-5">
                            <div>
                                <label className="block text-sm font-bold text-[#05144b] mb-2">Icon Name</label>
                                <input 
                                    type="text" 
                                    className="w-full bg-white border border-slate-200 rounded-lg px-4 py-2.5 text-sm text-[#05144b] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-medium"
                                    placeholder="e.g., Swimming Pool"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-bold text-[#05144b] mb-2">Upload Image</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-20 h-20 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-center overflow-hidden shrink-0">
                                        {iconPreview ? (
                                            <img src={getImageUrl(iconPreview)} alt="Preview" className="max-w-full max-h-full object-contain p-2" />
                                        ) : (
                                            <i className="fa-regular fa-image text-2xl text-slate-300"></i>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <label className="cursor-pointer bg-slate-50 hover:bg-slate-100 border border-dashed border-slate-300 rounded-xl px-4 py-3 flex flex-col items-center justify-center transition-colors">
                                            <i className="fa-solid fa-cloud-arrow-up text-blue-500 mb-1"></i>
                                            <span className="text-xs font-bold text-slate-600">Click to browse</span>
                                            <input 
                                                type="file" 
                                                className="hidden" 
                                                accept="image/*"
                                                onChange={handleFileChange}
                                            />
                                        </label>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="pt-4 flex justify-end gap-3 border-t border-slate-100">
                                <button 
                                    type="button" 
                                    onClick={handleCloseDialog}
                                    className="px-5 py-2.5 text-sm font-bold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="px-5 py-2.5 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-md transition-colors"
                                >
                                    {editMode ? 'Save Changes' : 'Upload Icon'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            <style>{`
                .animate-fade-in-up {
                    animation: fadeInUp 0.3s ease-out forwards;
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </AdminLayout>
    );
};

export default AdminIcons;
